// Pure HTML generator for the masthead-newsletter template.
//
// It emits a COMPLETE HTML document (doctype + head + body). crm-api's
// `campaign_email.html.erb` detects complete-HTML content and passes it through
// its strict allowlist verbatim (the `is_complete_html` branch), appending only
// the unsubscribe + open-tracking footer — so this file fully owns the visual
// chrome, exactly like the webinar + olive builders. No crm-api change needed.
//
// Modern editorial look — soft page, a rounded white card, a navy masthead with
// a lime accent stripe, a clean sans title under a short lime rule, an inset
// rounded hero, pill buttons, hairline dividers and a deep-navy footer — while
// staying fully Outlook-bulletproof (table-based, inline styles, VML buttons,
// bgcolor attributes, an `[if mso]` width ghost-table).

import { socialFooterLinksHtml } from "@crm-src/lib/email-social"
import {
  MASTHEAD_ACCENT,
  MASTHEAD_BODY,
  MASTHEAD_FOOTER_BG,
  MASTHEAD_HAIRLINE,
  MASTHEAD_LIME,
  MASTHEAD_MUTED,
  MASTHEAD_PAGE_BG,
  MASTHEAD_TINT,
  MASTHEAD_TITLE,
  XOP_BRAND_MARK_URL,
  toAbsoluteUrl,
  type MastheadContent,
  type MastheadSection,
  type MastheadTenantInfo,
} from "./types"

// Modern system-font stack — clean + sans (renders as Helvetica/Arial in mail).
const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif"

// HTML-escape a plain-text value before it goes into email markup. The rich-text
// body is intentionally NOT escaped (it's TipTap HTML, re-sanitized server-side
// by crm-api's `safe_campaign` allowlist).
const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string))

// Normalize a user-entered link to an absolute, http(s)-only href. Anything that
// isn't http/https (javascript:, data:, blank) collapses to "#" so a hostile
// value can't smuggle a scheme into a button/anchor. An empty link → "#": the
// register buttons ALWAYS render (send-time validation blocks an empty link).
export const safeHref = (url: string): string => {
  const v = (url || "").trim()
  if (!v) return "#"
  if (/^https?:\/\//i.test(v)) return v
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "#" // some other scheme → drop
  return `https://${v}` // bare domain → assume https
}

// A bulletproof (Outlook VML + standards) pill button. Reused by the top-right
// register button and the bottom CTA so they stay pixel-identical. ALWAYS
// rendered — a blank link points at "#" and is blocked before the campaign sends.
function buttonHtml(label: string, href: string, opts?: { width?: number }): string {
  const width = opts?.width ?? 210
  const safe = safeHref(href)
  return `
    <!--[if mso]>
    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${safe}" style="height:46px;v-text-anchor:middle;width:${width}px;" arcsize="50%" strokecolor="${MASTHEAD_ACCENT}" fillcolor="${MASTHEAD_ACCENT}">
      <w:anchorlock/>
      <center style="color:#ffffff;font-family:${FONT};font-size:15px;font-weight:bold;letter-spacing:0.4px;">${esc(label)}</center>
    </v:roundrect>
    <![endif]-->
    <!--[if !mso]><!-->
    <table cellpadding="0" cellspacing="0" border="0" style="margin:0;">
      <tr>
        <td align="center" bgcolor="${MASTHEAD_ACCENT}" style="background-color:${MASTHEAD_ACCENT}; border-radius:26px;">
          <a href="${safe}" target="_blank" style="display:inline-block; padding:14px 34px; color:#ffffff; font-size:15px; font-weight:bold; letter-spacing:0.4px; text-decoration:none; font-family:${FONT}; line-height:18px;">${esc(label)}</a>
        </td>
      </tr>
    </table>
    <!--<![endif]-->`
}

// The deep-navy footer: address line, opt-in line, tenant website, the ONE fixed
// "follow us" block (LinkedIn + YouTube CTA buttons from `email-social.ts`), and
// copyright. The unsubscribe link + tracking pixel are appended by crm-api after
// this document (do NOT duplicate them here).
function footerHtml(content: MastheadContent, tenant: MastheadTenantInfo | undefined, year: number): string {
  const company = tenant?.name || content.masthead || "XOP Networks"
  const addressBits = [tenant?.address, [tenant?.city, tenant?.state, tenant?.postal_code].filter(Boolean).join(", "), tenant?.country]
    .filter((s) => s && String(s).trim())
    .join(" &#183; ")
  const addressLine = addressBits ? `${esc(company)}&#160;&#160;|&#160;&#160;${addressBits}` : esc(company)

  return `
          <!-- Footer -->
          <tr>
            <td class="mobile-pad" bgcolor="${MASTHEAD_FOOTER_BG}" style="background-color:${MASTHEAD_FOOTER_BG}; padding:36px 44px 30px 44px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="center" style="text-align:center;">
                    <!-- lime accent pill -->
                    <table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto 18px auto;"><tr><td bgcolor="${MASTHEAD_LIME}" style="background-color:${MASTHEAD_LIME}; width:40px; height:4px; line-height:4px; font-size:0; border-radius:2px;">&#160;</td></tr></table>
                    <p style="color:#ffffff; font-size:13px; font-weight:bold; margin:0 0 14px 0; font-family:${FONT}; line-height:20px;">${addressLine}</p>
                    <p style="color:#9aa4bd; font-size:13px; margin:0 0 14px 0; font-family:${FONT}; line-height:20px;">
                      You received this email because you opted to receive updates${tenant?.name ? ` from ${esc(tenant.name)}` : ""}.
                    </p>
                    ${tenant?.website ? `<p style="color:#9aa4bd; font-size:13px; margin:0 0 16px 0; font-family:${FONT}; line-height:20px;"><a href="${safeHref(tenant.website)}" style="color:${MASTHEAD_LIME}; text-decoration:none; font-weight:bold;" target="_blank">${esc(tenant.website)}</a></p>` : ""}
                    ${socialFooterLinksHtml({ theme: "dark", marginStyle: "margin:20px 0 0 0;" })}
                    <p style="color:#5f6a86; font-size:11px; margin:18px 0 0 0; font-family:${FONT}; line-height:16px;">&copy; ${year} ${esc(company)}. All rights reserved.</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`
}

// Build the complete masthead-newsletter email HTML from its single section.
export function generateMastheadHtml(
  section: MastheadSection | undefined,
  tenant: MastheadTenantInfo | undefined,
  year: number,
): string {
  const c: MastheadContent = section?.content ?? ({} as MastheadContent)

  const announcement = (c.announcement || "").trim()
  const masthead = (c.masthead || tenant?.name || "XOP Networks").trim()
  const title = (c.title || "Your Headline Here").trim()
  const heroUrl = toAbsoluteUrl(c.imageUrl || "/stock-images/network-infrastructure.jpg")
  const sponsorUrl = (c.sponsorLogoUrl || "").trim()
  const brandMarkUrl = toAbsoluteUrl(XOP_BRAND_MARK_URL)
  const date = (c.date || "").trim()
  const startTime = (c.startTime || "").trim()
  const sponsoredBy = (c.sponsoredBy || "").trim()
  const registrationLink = (c.registrationLink || "").trim()
  const unableNote = (c.unableToAttendNote || "").trim()
  const body = c.body || "<p>Write your announcement here…</p>"

  // The left detail column (date / time / sponsored-by). Each row hides when blank.
  const detailRows = [
    date && `<tr><td style="padding:0 0 8px 0;"><span style="color:${MASTHEAD_MUTED}; font-size:11px; font-weight:bold; letter-spacing:0.8px; text-transform:uppercase; font-family:${FONT};">Date</span><br/><span style="color:${MASTHEAD_TITLE}; font-size:15px; font-family:${FONT};">${esc(date)}</span></td></tr>`,
    startTime && `<tr><td style="padding:0 0 8px 0;"><span style="color:${MASTHEAD_MUTED}; font-size:11px; font-weight:bold; letter-spacing:0.8px; text-transform:uppercase; font-family:${FONT};">Start Time</span><br/><span style="color:${MASTHEAD_TITLE}; font-size:15px; font-family:${FONT};">${esc(startTime)}</span></td></tr>`,
    sponsoredBy && `<tr><td style="padding:0;"><span style="color:${MASTHEAD_MUTED}; font-size:11px; font-weight:bold; letter-spacing:0.8px; text-transform:uppercase; font-family:${FONT};">Sponsored by</span><br/><span style="color:${MASTHEAD_TITLE}; font-size:15px; font-family:${FONT};">${esc(sponsoredBy)}</span></td></tr>`,
  ]
    .filter(Boolean)
    .join("")

  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${esc(title)}</title>
  <!--[if gte mso 9]>
  <xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml>
  <![endif]-->
  <style type="text/css">
    body { margin:0; padding:0; -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%; }
    table { border-collapse:collapse; mso-table-lspace:0pt; mso-table-rspace:0pt; }
    img { border:0; height:auto; line-height:100%; outline:none; text-decoration:none; -ms-interpolation-mode:bicubic; }
    a { color:${MASTHEAD_ACCENT}; }
    .m-body a { color:${MASTHEAD_ACCENT}; font-weight:bold; }
    @media only screen and (max-width:620px) {
      .email-container { width:100% !important; max-width:100% !important; border-radius:0 !important; }
      .mobile-stack { display:block !important; width:100% !important; max-width:100% !important; text-align:center !important; }
      .mobile-pad { padding-left:24px !important; padding-right:24px !important; }
      .mobile-center { text-align:center !important; }
      .mobile-gap { padding-top:18px !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0; font-family:${FONT}; background-color:${MASTHEAD_PAGE_BG};">
  <!-- Outlook needs bgcolor ATTRIBUTES (it ignores the page <body> background). -->
  <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${MASTHEAD_PAGE_BG}" style="background-color:${MASTHEAD_PAGE_BG};">
    <tr>
      <td align="center" style="padding:30px 12px;">
        <!--[if mso]>
        <table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" width="640"><tr><td>
        <![endif]-->
        <table class="email-container" align="center" width="640" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="background-color:#ffffff; max-width:640px; width:100%; border-radius:16px; overflow:hidden; box-shadow:0 12px 30px rgba(20,29,51,0.10);">

          ${
            announcement
              ? `
          <!-- Announcement bar -->
          <tr>
            <td class="mobile-pad" bgcolor="${MASTHEAD_TINT}" style="background-color:${MASTHEAD_TINT}; padding:13px 44px; text-align:center; border-bottom:1px solid ${MASTHEAD_HAIRLINE};">
              <p style="margin:0; color:#4a5675; font-size:13px; line-height:19px; font-family:${FONT};">${esc(announcement)}</p>
            </td>
          </tr>`
              : ""
          }

          <!-- Masthead -->
          <tr>
            <td class="mobile-pad" bgcolor="${MASTHEAD_ACCENT}" style="background-color:${MASTHEAD_ACCENT}; padding:24px 44px;">
              <table cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="vertical-align:middle; padding-right:14px;" valign="middle">
                    <img src="${brandMarkUrl}" alt="${esc(masthead)}" height="38" style="height:38px; width:auto; display:block; border:0;" />
                  </td>
                  <td style="vertical-align:middle;" valign="middle">
                    <p style="margin:0; color:#ffffff; font-size:25px; font-weight:bold; letter-spacing:0.2px; line-height:30px; font-family:${FONT};">${esc(masthead)}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- lime accent stripe under the masthead -->
          <tr><td bgcolor="${MASTHEAD_LIME}" style="background-color:${MASTHEAD_LIME}; height:4px; line-height:4px; font-size:0;">&#160;</td></tr>

          <!-- Title block -->
          <tr>
            <td class="mobile-pad" style="padding:34px 44px 8px 44px;">
              <table cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${MASTHEAD_LIME}" style="background-color:${MASTHEAD_LIME}; width:46px; height:4px; line-height:4px; font-size:0; border-radius:2px;">&#160;</td></tr></table>
              <h1 style="margin:16px 0 0 0; color:${MASTHEAD_TITLE}; font-size:27px; font-weight:bold; line-height:34px; letter-spacing:-0.3px; font-family:${FONT};">${esc(title)}</h1>
            </td>
          </tr>

          <!-- Hero image (inset, rounded) -->
          <tr>
            <td class="mobile-pad" style="padding:22px 44px 8px 44px;">
              <img src="${heroUrl}" alt="${esc(title)}" width="552" style="width:100%; max-width:552px; height:auto; display:block; border:0; border-radius:12px;" />
              ${
                sponsorUrl
                  ? `
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="right" style="padding:10px 2px 0 0; text-align:right;">
                    <table cellpadding="0" cellspacing="0" border="0" style="display:inline-block;">
                      <tr>
                        <td style="vertical-align:middle; padding-right:9px;" valign="middle"><span style="color:${MASTHEAD_MUTED}; font-size:11px; letter-spacing:0.6px; text-transform:uppercase; font-family:${FONT};">Presented by</span></td>
                        <td style="vertical-align:middle;" valign="middle"><img src="${toAbsoluteUrl(sponsorUrl)}" alt="Sponsor" height="28" style="height:28px; width:auto; display:block; border:0;" /></td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>`
                  : ""
              }
            </td>
          </tr>

          <!-- Details + register row (button ALWAYS shows) -->
          <tr>
            <td class="mobile-pad" style="padding:22px 44px 18px 44px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td class="mobile-stack" width="58%" style="width:58%; vertical-align:middle; text-align:left;" valign="middle" align="left">
                    ${detailRows ? `<table cellpadding="0" cellspacing="0" border="0">${detailRows}</table>` : "&#160;"}
                  </td>
                  <td class="mobile-stack mobile-gap" width="42%" style="width:42%; vertical-align:middle; text-align:right;" valign="middle" align="right">
                    ${buttonHtml("REGISTER NOW", registrationLink)}
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Divider -->
          <tr><td class="mobile-pad" style="padding:0 44px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid ${MASTHEAD_HAIRLINE}; font-size:0; line-height:0;">&#160;</td></tr></table></td></tr>

          ${
            unableNote
              ? `
          <!-- Unable to attend note -->
          <tr>
            <td class="mobile-pad" style="padding:18px 44px;">
              <p style="margin:0; color:${MASTHEAD_BODY}; font-size:14px; line-height:22px; font-family:${FONT};"><span style="color:${MASTHEAD_TITLE}; font-weight:bold;">Unable to attend?</span> ${esc(unableNote)}</p>
            </td>
          </tr>
          <tr><td class="mobile-pad" style="padding:0 44px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid ${MASTHEAD_HAIRLINE}; font-size:0; line-height:0;">&#160;</td></tr></table></td></tr>`
              : ""
          }

          <!-- Body -->
          <tr>
            <td class="mobile-pad m-body" style="padding:26px 44px 8px 44px; color:${MASTHEAD_BODY}; font-size:15px; line-height:24px; font-family:${FONT};">
              ${body}
            </td>
          </tr>

          <!-- Bottom CTA (always shows) -->
          <tr>
            <td style="padding:18px 44px 40px 44px; text-align:center;" align="center">
              ${buttonHtml("REGISTER NOW", registrationLink)}
            </td>
          </tr>

          ${footerHtml(c, tenant, year)}

        </table>
        <!--[if mso]>
        </td></tr></table>
        <![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>`
}
